import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Landing.css';
import { useConfig } from '../ConfigContext';

function Landing() {
  const config = useConfig();
  const lp = config.landingPageContent;
  const lv = config.landingPage ?? {};

  const [expandedUseCase, setExpandedUseCase] = useState(null);
  const [codeExample, setCodeExample] = useState('mic');

  // ── CONFIG-DRIVEN DATA ──────────────────────────────────────

  const heroStats = lp.heroStats ?? [
    { value: '0',        label: 'Training samples required' },
    { value: '<50ms',    label: 'Detection latency' },
    { value: 'Same day', label: 'Time to deploy' },
  ];

  // Icons are hardcoded here — not stored in admin config — so they always apply
  // regardless of whether the server has overridden the use case text.
  const UC_ICONS = [
    '/icons/landing/iot.png',
    '/icons/landing/defense.png',
    '/icons/landing/healthcare.png',
    '/icons/landing/robot.png',
    '/icons/landing/enterprise.png',
    '/icons/landing/accessibility.png',
  ];

  const useCases = (lp.useCases ?? []).length > 0
    ? lp.useCases.map((uc, i) => ({ id: i + 1, tag: uc.industry, industry: uc.industry, description: uc.description, icon: UC_ICONS[i] ?? null }))
    : [
        { id: 1, tag: 'IoT',        industry: 'Smart Devices & IoT',   icon: UC_ICONS[0], description: 'Wake word and voice command detection on embedded hardware — no audio samples, no vendor lock-in, ready to ship the same day.' },
        { id: 2, tag: 'Defense',    industry: 'Defense & Government',  icon: UC_ICONS[1], description: 'Accurate recognition of callsigns, operational terms, and unit names in fully air-gapped environments with no audio leaving the device.' },
        { id: 3, tag: 'Medical',    industry: 'Healthcare',            icon: UC_ICONS[2], description: 'Drug names, procedure codes, and specialist terminology captured correctly — configured at runtime without retraining or annotation pipelines.' },
        { id: 4, tag: 'Robotics',   industry: 'Robotics & Automation', icon: UC_ICONS[3], description: 'Natural voice commands — start, stop, and domain-specific triggers — with multi-keyword monitoring and noise-robust detection.' },
        { id: 5, tag: 'Enterprise', industry: 'Enterprise & Legal',    icon: UC_ICONS[4], description: 'Proprietary product names, legal terms, and internal jargon transcribed exactly as defined — every time, without touching the model.' },
        { id: 6, tag: 'Access',     industry: 'Accessibility',         icon: UC_ICONS[5], description: 'Voice interfaces that work immediately across diverse speakers, accents, and speech patterns without per-user recording sessions.' },
      ];

  const sharedPrinciples = (lp.principles ?? []).length > 0
    ? lp.principles
    : [
        { label: 'No Training Data',    detail: 'Define keywords or vocabulary in plain text. No audio samples, no annotation work. Detection and transcription are active from the first utterance.' },
        { label: 'Edge + Cloud',        detail: 'The same pipeline runs on embedded hardware, air-gapped devices, and cloud infrastructure — same API, same accuracy, wherever you need it.' },
        { label: 'Deploy the Same Day', detail: 'No retraining cycle, no redeployment. Add or change keywords and vocabulary at runtime. Go from definition to live detection in minutes.' },
      ];

  const deploymentModes = [
    {
      id: 'ondevice',
      label: 'On-Device',
      name: 'On-Device Deployment',
      icon: '/icons/landing/on-device.png',
      tagline: lp.deployOnDeviceTagline ?? 'Run the complete pipeline on embedded hardware, air-gapped systems, or any CPU-only environment — with no network dependency and no audio leaving the device.',
      features: (lp.deployOnDeviceFeatures ?? []).slice(0, 3).length > 0
        ? (lp.deployOnDeviceFeatures ?? []).slice(0, 3)
        : [
            'CPU-optimized — no GPU or cloud connection required',
            'Air-gapped and fully offline capable',
            'Supports on-chip, Linux, Android, iOS, and Windows',
          ],
      cardClass: 'lt-deploy-ondevice',
      link: '/ondevice',
      linkLabel: 'Explore On-Device →',
    },
    {
      id: 'cloud',
      label: 'Cloud',
      name: 'Cloud Deployment',
      icon: '/icons/landing/cloud.png',
      tagline: lp.deployCloudTagline ?? 'Scale to GPU-backed infrastructure for higher throughput and maximum accuracy. Built for production workloads — concurrent streams, batch processing, and larger models.',
      features: (lp.deployCloudFeatures ?? []).slice(0, 3).length > 0
        ? (lp.deployCloudFeatures ?? []).slice(0, 3)
        : [
            'GPU-accelerated inference for high-throughput workloads',
            'Larger, higher-accuracy models with the same API',
            'Full control over your infrastructure and data',
          ],
      cardClass: 'lt-deploy-cloud',
      link: '/cloud',
      linkLabel: 'Explore Cloud →',
    },
  ];

  const prymeCardFeatures = ((lp.prymeCardFeatures ?? []).length > 0
    ? lp.prymeCardFeatures
    : [
        'Detect any keyword from a text label alone — no audio required',
        'Multi-stage proprietary detection engine',
        'Edge hardware or cloud deployment, same API',
      ]).slice(0, 3);

  const apexCardFeatures = ((lp.apexCardFeatures ?? []).length > 0
    ? lp.apexCardFeatures
    : [
        'Calibrated to your vocabulary with a small set of audio examples',
        'Higher accuracy on stable, known terms and wake words',
        'CPU edge or GPU cloud deployment',
      ]).slice(0, 3);

  const ctaChecklist = (lp.ctaChecklist ?? []).length > 0
    ? lp.ctaChecklist
    : [
        'Keyword detection from text labels alone',
        'Domain transcription with vocabulary calibration',
        'Edge, on-chip, or cloud deployment',
        'Runtime vocabulary changes — no redeployment',
        'Deploy the same day',
      ];

  // ── CODE SNIPPETS ──────────────────────────────────────────
  const codeSnippets = {
    pryme: {
      mic: `from tytonvoice import KeywordDetector, DetectorConfig, MicLoopController

detector = KeywordDetector(config=DetectorConfig(
    labels=["start engine", "stop", "emergency"],
))

MicLoopController().start(
    detector=detector,
    callback=lambda r: print(r.top_label) if r.triggered else None,
)`,

      file: `from tytonvoice import KeywordDetector, DetectorConfig, WavLoopController

detector = KeywordDetector(config=DetectorConfig(
    labels=["yes", "no", "cancel"],
))

WavLoopController().start(
    file_path="recording.wav",
    detector=detector,
    callback=lambda r: print(r.top_label, r.top_score),
)`,

      transcribe: `from tytonvoice import Transcriber, TranscriberConfig, MicLoopController

transcriber = Transcriber(config=TranscriberConfig())

MicLoopController().start(
    detector=transcriber,
    callback=lambda r: print(r.text),
)`,
    },
  };

  // Lightweight Python syntax highlighter
  const highlight = (code) => {
    const escape = (s) => s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const lines = code.split('\n');
    return lines.map(line => {
      if (/^\s*#/.test(line)) return `<span class="ltc-comment">${escape(line)}</span>`;
      let out = '';
      let i = 0;
      const src = line;
      while (i < src.length) {
        if (src.slice(i,i+3) === '"""') {
          let j = src.indexOf('"""', i+3);
          j = j === -1 ? src.length : j+3;
          out += `<span class="ltc-string">${escape(src.slice(i,j))}</span>`; i = j; continue;
        }
        if (src[i] === '"' || src[i] === "'") {
          const q = src[i]; let j = i+1;
          while (j < src.length && src[j] !== q) { if (src[j] === '\\') j++; j++; }
          j++;
          out += `<span class="ltc-string">${escape(src.slice(i,j))}</span>`; i = j; continue;
        }
        if (src[i] === '#') { out += `<span class="ltc-comment">${escape(src.slice(i))}</span>`; break; }
        if (/[a-zA-Z_]/.test(src[i])) {
          let j = i;
          while (j < src.length && /[\w]/.test(src[j])) j++;
          const word = src.slice(i, j);
          const kwds = ['from','import','def','class','return','if','else','elif','for','while','in','not','and','or','True','False','None','print','with','as','try','except','raise','pass','lambda','yield','async','await'];
          const bltns = ['str','int','float','list','dict','bool','len','range','type','isinstance'];
          if (kwds.includes(word)) out += `<span class="ltc-kw">${word}</span>`;
          else if (bltns.includes(word)) out += `<span class="ltc-builtin">${word}</span>`;
          else if (/^[A-Z]/.test(word)) out += `<span class="ltc-class">${word}</span>`;
          else if (j < src.length && src[j] === '(') out += `<span class="ltc-fn">${word}</span>`;
          else out += escape(word);
          i = j; continue;
        }
        if (/[0-9]/.test(src[i])) {
          let j = i;
          while (j < src.length && /[0-9.]/.test(src[j])) j++;
          out += `<span class="ltc-num">${escape(src.slice(i,j))}</span>`; i = j; continue;
        }
        out += escape(src[i]); i++;
      }
      return out;
    }).join('\n');
  };

  return (
    <div className="lt-page">

      {/* ── HERO ── */}
      {(lv.hero ?? true) && (
        <section className="lt-hero">
          <div className="lt-hero-content">
            <div className="lt-hero-eyebrow">Tytonvoice</div>
            <h1 className="lt-hero-title">
              {lp.heroTitle ?? 'A unified voice intelligence platform – from silicon to cloud'}
            </h1>
            <p className="lt-hero-subtitle">
              {lp.heroSubtitle ?? 'Tytonvoice delivers ultra-low-latency wake-word detection, keyword recognition, and high-accuracy transcription — configurable at runtime, deployable anywhere from embedded chips to cloud infrastructure.'}
            </p>
            <div className="lt-hero-cta-group">
              <Link to="/pryme#try-pryme-live" className="lt-btn-primary">
                Try Live Demo
              </Link>
              <a
                href={config.navbar.contactUs.url}
                target="_blank"
                rel="noopener noreferrer"
                className="lt-btn-secondary"
              >
                Contact Us
              </a>
            </div>
            <div className="lt-hero-stats">
              {heroStats.map((stat, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <div className="lt-stat-divider" />}
                  <div className="lt-stat">
                    <span className="lt-stat-value">{stat.value}</span>
                    <span className="lt-stat-label">{stat.label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
          {(lp.heroImage || (lv.showPlaceholders ?? true)) && (
            <div className="lt-hero-visual">
              {lp.heroImage
                ? <img src={lp.heroImage} alt="Hero Visual" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <>
                    <span className="lt-ph-label">Hero Visual</span>
                    <span className="lt-ph-sub">Product demo video / animation</span>
                  </>
              }
            </div>
          )}
        </section>
      )}

      {/* ── PROBLEM STATEMENT ── */}
      {(lv.problem ?? true) && (
        <section className="lt-problem">
          <div className="lt-problem-inner">
            <p className="lt-problem-text">
              {lp.problemText
                ? (() => {
                    const oldPhrase = 'We cut that to zero';
                    const newPhrase = 'We reduce that to nothing';
                    const text = lp.problemText.replace(oldPhrase, newPhrase);
                    const index = text.lastIndexOf(newPhrase);
                    if (index === -1) return text;
                    const beforeBlue = text.substring(0, index);
                    return (
                      <>
                        {beforeBlue}
                        <span className="lt-problem-highlight">{newPhrase}</span>
                      </>
                    );
                  })()
                : <>
                    Building voice-enabled products typically means collecting hundreds of audio samples,
                    training custom models, and waiting weeks before anything works.
                    Every vocabulary change restarts the process.{' '}
                    <strong>Tytonvoice eliminates that cycle entirely.</strong>
                  </>
              }
            </p>
          </div>
        </section>
      )}

      {/* ── PRODUCTS ── */}
      {(lv.products ?? true) && (
        <section className="lt-products">
          <div className="lt-section-header">
            <h2>Two Solutions. One Platform.</h2>
            <p className="lt-section-subtitle">
              {config.branding.zslName} and {config.branding.fslName} solve different problems in the voice AI stack,
              built on the same engine and deployable anywhere.
            </p>
          </div>
          <div className="lt-products-grid">

            {/* Pryme card */}
            <div className="lt-product-card lt-product-pryme">
              <div className="lt-product-card-header">
                <span className="lt-product-badge lt-badge-pryme">{config.branding.zslName}</span>
                <h3 className="lt-product-name">Keyword Detection</h3>
                <p className="lt-product-tagline">
                  {lp.prymeCardTagline ?? 'Define any keyword or phrase in plain text and start detecting immediately — no audio samples, no training pipeline, no wait.'}
                </p>
              </div>
              <ul className="lt-product-features">
                {prymeCardFeatures.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
              <div className="lt-product-card-footer">
                <Link to="/pryme" className="lt-product-link">
                  Explore {config.branding.zslName} →
                </Link>
                <Link to="/pryme#try-pryme-live" className="lt-product-demo">
                  Try live demo
                </Link>
              </div>
            </div>

            {/* Apex card */}
            <div className="lt-product-card lt-product-apex">
              <div className="lt-product-card-header">
                <span className="lt-product-badge lt-badge-apex">{config.branding.fslName}</span>
                <h3 className="lt-product-name">Domain Voice Accuracy</h3>
                <p className="lt-product-tagline">
                  {lp.apexCardTagline ?? 'Bring higher accuracy to detection and transcription by calibrating to your exact vocabulary — wake words, domain terms, and operational language — with a small set of audio examples.'}
                </p>
              </div>
              <ul className="lt-product-features">
                {apexCardFeatures.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
              <div className="lt-product-card-footer">
                <Link to="/apex" className="lt-product-link">
                  Explore {config.branding.fslName} →
                </Link>
              </div>
            </div>

          </div>
        </section>
      )}

      {/* ── SHARED PRINCIPLES ── */}
      {(lv.principles ?? true) && (
        <section className="lt-principles">
          <div className="lt-principles-inner">
            {sharedPrinciples.map((p, i) => (
              <div key={i} className="lt-principle">
                <div className="lt-principle-num">{String(i + 1).padStart(2, '0')}</div>
                <div className="lt-principle-label">{p.label}</div>
                <div className="lt-principle-detail">{p.detail}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── USE CASES ── */}
      {(lv.useCases ?? true) && (
        <section className="lt-use-cases">
          <div className="lt-section-header">
            <h2>Built for Real-World Deployment</h2>
            <p className="lt-section-subtitle">
              Any domain that needs reliable voice intelligence without building a custom model
            </p>
          </div>
          <div className="lt-use-cases-grid">
            {useCases.map((uc) => (
              <div
                key={uc.id}
                className={`lt-uc-card ${expandedUseCase === uc.id ? 'expanded' : ''}`}
                onClick={() => setExpandedUseCase(expandedUseCase === uc.id ? null : uc.id)}
              >
                <div className="lt-uc-top">
                  <div className="lt-uc-top-row">
                    <div>
                      <span className="lt-uc-badge">{uc.tag ?? uc.industry}</span>
                      <h3 className="lt-uc-industry">{uc.industry}</h3>
                    </div>
                    {uc.icon && <img src={uc.icon} alt={uc.industry} className="lt-uc-icon" />}
                  </div>
                </div>
                <p className="lt-uc-description">{uc.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── DEPLOYMENT ── */}
      {(lv.deployment ?? true) && (
        <section className="lt-platforms">
          <div className="lt-section-header">
            <h2>Runs Where You Need It</h2>
            <p className="lt-section-subtitle">
              The same pipeline — edge-optimized or cloud-scaled, on any platform
            </p>
          </div>
          <div className="lt-deploy-grid">
            {deploymentModes.map((mode) => (
              <div key={mode.id} className={`lt-deploy-card ${mode.cardClass}`}>
                <div className="lt-deploy-header">
                  <div className="lt-deploy-header-row">
                    <div className="lt-deploy-header-text">
                      <span className="lt-deploy-label">{mode.label}</span>
                      <h3 className="lt-deploy-name">{mode.name}</h3>
                    </div>
                    {mode.icon && (
                      <img src={mode.icon} alt={mode.label} className="lt-deploy-icon" />
                    )}
                  </div>
                  <p className="lt-deploy-tagline">{mode.tagline}</p>
                </div>
                <ul className="lt-deploy-features">
                  {mode.features.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
                <Link to={mode.link} className="lt-deploy-link">{mode.linkLabel}</Link>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── CODE WINDOW ── */}
      {(lv.codeWindow ?? true) && (
        <section className="lt-code-section">
          <div className="lt-code-header">
            <div className="lt-code-title-block">
              <p className="lt-code-eyebrow">Start building</p>
              <h2 className="lt-code-heading">
                {lp.codeSectionHeading ?? 'Up and running in minutes'}
              </h2>
              <p className="lt-code-subheading">
                {lp.codeSectionSubheading ?? 'Install the SDK, define your keywords in plain text, and start detecting. No training pipeline. No redeployment cycle.'}
              </p>
              <div className="lt-code-install">
                <span className="lt-code-install-label">pip install tytonvoice</span>
                <button
                  className="lt-code-copy-btn"
                  onClick={() => navigator.clipboard.writeText('pip install tytonvoice')}
                  title="Copy to clipboard"
                >
                  ⧉
                </button>
              </div>
              <div className="lt-code-cta-links">
                <a
                  href={config.navbar.contactUs.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lt-btn-primary"
                >
                  Request SDK Access
                </a>
                <Link to="/pryme#try-pryme-live" className="lt-product-demo">
                  Try live demo →
                </Link>
              </div>
            </div>
          </div>

          <div className="lt-code-window">
            {/* Window chrome */}
            <div className="lt-code-chrome">
              <div className="lt-code-chrome-dots">
                <span /><span /><span />
              </div>
              <div className="lt-code-example-tabs">
                    <button
                      className={`lt-code-tab ${codeExample === 'mic' ? 'active' : ''}`}
                      onClick={() => setCodeExample('mic')}
                    >
                      mic_detection.py
                    </button>
                    <button
                      className={`lt-code-tab ${codeExample === 'file' ? 'active' : ''}`}
                      onClick={() => setCodeExample('file')}
                    >
                      file_detection.py
                    </button>
                    <button
                      className={`lt-code-tab ${codeExample === 'transcribe' ? 'active' : ''}`}
                      onClick={() => setCodeExample('transcribe')}
                    >
                      transcription.py
                    </button>
              </div>
            </div>

            {/* Code body */}
            <div className="lt-code-body">
              <pre className="lt-code-pre">
                <code dangerouslySetInnerHTML={{ __html: highlight(codeSnippets['pryme']?.[codeExample] || '') }} />
              </pre>
            </div>
          </div>
        </section>
      )}

      {/* ── CTA ── */}
      {(lv.cta ?? true) && (<section className="lt-cta">
        <div className="lt-cta-inner">
          <div className="lt-cta-left">
            <p className="lt-cta-eyebrow">Get started</p>
            <div className="lt-cta-heading">
              <div className="lt-cta-brand">Tytonvoice</div>
            </div>
            <p className="lt-cta-body">
              Voice intelligence that deploys the same day — from embedded hardware to cloud infrastructure.
              No annotation work. No retraining cycles. Just define your vocabulary and go.
            </p>
            <div className="lt-cta-buttons">
              <a
                href={config.navbar.contactUs.url}
                target="_blank"
                rel="noopener noreferrer"
                className="lt-btn-primary"
              >
                Contact Us
              </a>
              <Link to="/pryme#try-pryme-live" className="lt-btn-secondary">
                Try Live Demo
              </Link>
            </div>
          </div>
          <div className="lt-cta-right">
            <ul className="lt-cta-checklist">
              {ctaChecklist.map((item, i) => (
                <li key={i} className="lt-cta-check-item">
                  <span className="lt-cta-check-icon">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>)}

    </div>
  );
}

export default Landing;